"use client"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  CasinoIcon,
  LiveIcon,
  OriginalsIcon,
  PopularIcon,
  SlotsIcon,
  type IconComponent
} from "@/src/shared/assets/icons"

import { lobbyChipIds, type LobbyChipId } from "../data/lobby"
import { lobbyTexts } from "../localization/texts"

export type CategoryChipsProps = {
  selected: LobbyChipId | null
  onSelect: (id: LobbyChipId | null) => void
}

const chipIcons: Record<LobbyChipId, { Icon: IconComponent; viewBox: string }> =
  {
    originals: { Icon: OriginalsIcon, viewBox: "-2.5 -2 24 24" },
    slots: { Icon: SlotsIcon, viewBox: "-3 -3 24 24" },
    blackjack: { Icon: CasinoIcon, viewBox: "0 0 24 24" },
    live: { Icon: LiveIcon, viewBox: "0 0 24 24" },
    popular: { Icon: PopularIcon, viewBox: "-2.5 -2 24 24" }
  }

const wideLayoutChips: ReadonlySet<LobbyChipId> = new Set([
  "originals",
  "popular"
])

export function CategoryChips({ selected, onSelect }: CategoryChipsProps) {
  return (
    <section aria-label={lobbyTexts.chipsAria}>
      <div
        className="flex min-w-0 items-center gap-1 md:gap-2"
        role="group"
      >
        {lobbyChipIds.map((id) => {
          const { Icon, viewBox } = chipIcons[id]
          const isSelected = selected === id
          const isWideOnly = wideLayoutChips.has(id)

          return (
            <Button
              key={id}
              type="button"
              variant="primary"
              className={cn(
                "w-auto grow basis-auto overflow-hidden border border-white/10 bg-black-800 px-3 py-0 font-title text-sm leading-none shadow-none hover:bg-black-800 md:justify-center md:text-sm xlg:text-lg",
                "focus-visible:ring-0 focus-visible:ring-offset-0",
                id === "live" ? "shrink-0 grow-0 md:grow" : "min-w-0 shrink",
                (id === "slots" || id === "blackjack") && "px-1 md:px-3",
                isSelected &&
                  "border-purple-200 text-white [&_path]:[fill-opacity:1]",
                isWideOnly && "hidden md:flex"
              )}
              aria-pressed={isSelected}
              onClick={() => {
                onSelect(isSelected ? null : id)
              }}
            >
              <span className="flex w-full min-w-0 items-center justify-center gap-2.5">
                <Icon
                  viewBox={viewBox}
                  overflow="hidden"
                  className="block size-6 shrink-0"
                  aria-hidden
                />
                <span className="min-w-0 truncate leading-none">
                  {lobbyTexts.chips[id]}
                </span>
              </span>
            </Button>
          )
        })}
      </div>
    </section>
  )
}
